import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AlertTriangle, X, Calculator, Mail } from 'lucide-react';
export function FinalPostscriptsSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-20 px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-920">
      {/* Background effects - matching reference snippet */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-921" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-red-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-922" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-923" />

      <div ref={ref} className="relative z-10 max-w-4xl mx-auto" data-id="element-924">
        {/* P.S. - THE MISINFORMATION ERA IS ACCELERATING */}
        <div className="relative mb-16" data-id="element-925">
          <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-926" />

          <div className="relative bg-gradient-to-br from-amber-950/50 via-stone-950/80 to-black border-2 border-amber-600/60 p-8 md:p-10 rounded-lg shadow-[0_0_50px_rgba(217,119,6,0.4)]" data-id="element-927">
            {/* Icon */}
            <div className="flex justify-center mb-6" data-id="element-928">
              <div className="relative" data-id="element-929">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-930" />
                <AlertTriangle className="relative w-12 h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(217,119,6,0.8)]" data-id="element-931" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-3xl md:text-4xl font-bold text-amber-400 mb-6 text-center" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.5)'
          }} data-id="element-932">
              P.S. – The Misinformation Era Is Accelerating
            </h3>

            {/* Content */}
            <div className="space-y-4 text-lg text-stone-300 mb-8" data-id="element-933">
              <p className="text-stone-100" data-id="element-934">
                Every day that passes, the gap between signal and noise gets
                wider.
              </p>
              <p className="text-stone-100" data-id="element-935">
                Every day you wait, more dangerous misinformation compounds.
              </p>
              <p className="text-stone-100" data-id="element-936">
                Every day you remain in the competence gap, your biology decays
                irreversibly.
              </p>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-6 my-8" data-id="element-937">
                <p className="text-xl font-bold text-amber-400 mb-4" data-id="element-938">
                  You have two choices:
                </p>
                <div className="space-y-4" data-id="element-939">
                  <p className="text-stone-100" data-id="element-940">
                    <span className="font-bold text-red-400" data-id="element-941">1)</span> Continue
                    piecing together fragments from influencers who don't
                    actually understand the source material
                  </p>
                  <p className="text-stone-100" data-id="element-942">
                    <span className="font-bold text-green-400" data-id="element-943">2)</span> Get
                    direct access to the two people who literally wrote THE book
                    on peptides... PLUS get Peptide Mastery 2.0 included free...
                    PLUS make the paradigm shift that changes everything
                  </p>
                </div>
              </div>

              <div className="text-center space-y-4 mt-8" data-id="element-944">
                <p className="text-xl font-bold text-stone-100" data-id="element-945">
                  The back door is open.
                </p>
                <p className="text-xl text-stone-100" data-id="element-946">
                  But it closes on{' '}
                  <span className="text-red-400 font-bold" data-id="element-947">
                    Friday, January 30th at 11:59PM ET
                  </span>
                  .
                </p>
                <p className="text-xl font-bold text-red-400" data-id="element-948">
                  And it will NEVER open again at this price.
                </p>
              </div>
            </div>

            {/* CTA */}
            <a href="https://go.peptidescienceinstitute.com/pm-pro-annual-fm" className="block w-full px-8 py-6 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white text-center font-bold text-xl rounded-lg transition-all duration-300 shadow-[0_0_40px_rgba(217,119,6,0.5)] hover:shadow-[0_0_60px_rgba(217,119,6,0.7)] hover:scale-105" data-id="element-949">
              → STEP THROUGH THE BACK DOOR NOW ←
            </a>
          </div>
        </div>

        {/* P.P.S. - WHAT IF YOU DO NOTHING? */}
        <div className="relative mb-16" data-id="element-950">
          <div className="absolute inset-0 bg-gradient-radial from-red-600/20 via-transparent to-transparent blur-3xl" data-id="element-951" />

          <div className="relative bg-gradient-to-br from-red-950/50 via-stone-950/80 to-black border-2 border-red-600/60 p-8 md:p-10 rounded-lg shadow-[0_0_50px_rgba(239,68,68,0.4)]" data-id="element-952">
            {/* Title */}
            <h3 className="text-3xl md:text-4xl font-bold text-red-400 mb-6 text-center" style={{
            textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
          }} data-id="element-953">
              P.P.S. – What If You Do Nothing?
            </h3>

            {/* Content */}
            <div className="space-y-4 text-lg mb-8" data-id="element-954">
              <p className="text-xl text-stone-100 text-center mb-6" data-id="element-955">
                Six months from now, if you chose not to take action today:
              </p>

              <div className="space-y-3" data-id="element-956">
                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-957">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-958" />
                  <p className="text-stone-100" data-id="element-959">
                    You'll still be in the competence gap
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-960">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-961" />
                  <p className="text-stone-100" data-id="element-962">
                    You'll still be guessing about your protocols
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-963">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-964" />
                  <p className="text-stone-100" data-id="element-965">
                    You'll still be wondering if you're making mistakes that
                    won't show up for years
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-966">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-967" />
                  <p className="text-stone-100" data-id="element-968">
                    You'll still be operating at the same biological level
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-969">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-970" />
                  <p className="text-stone-100" data-id="element-971">
                    You'll still be six months older with six months of
                    irreversible decay
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-972">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-973" />
                  <p className="text-stone-100" data-id="element-974">
                    You'll have NEVER made the paradigm shift
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-975">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-976" />
                  <p className="text-stone-100" data-id="element-977">
                    You'll have NEVER discovered what's truly possible
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-978">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-979" />
                  <p className="text-stone-100" data-id="element-980">
                    You'll STILL be relying on fragmented information from
                    people who don't know the full picture
                  </p>
                </div>
              </div>

              <div className="text-center space-y-4 mt-8" data-id="element-981">
                <p className="text-xl font-bold text-stone-100" data-id="element-982">
                  And you'll wish you had taken action when you had the chance.
                </p>
                <p className="text-2xl font-bold text-red-400" data-id="element-983">
                  Don't let that be your story.
                </p>
              </div>
            </div>

            {/* CTA */}
            <a href="https://go.peptidescienceinstitute.com/pm-pro-annual-fm" className="block w-full px-8 py-6 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-center font-bold text-xl rounded-lg transition-all duration-300 shadow-[0_0_40px_rgba(239,68,68,0.5)] hover:shadow-[0_0_60px_rgba(239,68,68,0.7)] hover:scale-105" data-id="element-984">
              → SECURE YOUR FOUNDING MEMBER STATUS NOW ←
            </a>
          </div>
        </div>

        {/* P.P.P.S. - A THOUGHT EXPERIMENT */}
        <div className="relative mb-16" data-id="element-985">
          <div className="absolute inset-0 bg-gradient-radial from-blue-600/20 via-transparent to-transparent blur-3xl" data-id="element-986" />

          <div className="relative bg-gradient-to-br from-blue-950/50 via-stone-950/80 to-black border-2 border-blue-600/60 p-8 md:p-10 rounded-lg shadow-[0_0_50px_rgba(59,130,246,0.4)]" data-id="element-987">
            {/* Icon */}
            <div className="flex justify-center mb-6" data-id="element-988">
              <div className="relative" data-id="element-989">
                <div className="absolute inset-0 bg-blue-500/40 blur-xl animate-pulse-slow" data-id="element-990" />
                <Calculator className="relative w-12 h-12 text-blue-400 drop-shadow-[0_0_15px_rgba(59,130,246,0.8)]" data-id="element-991" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-3xl md:text-4xl font-bold text-blue-400 mb-6 text-center" style={{
            textShadow: '0 0 30px rgba(59, 130, 246, 0.5)'
          }} data-id="element-992">
              P.P.P.S. – A Thought Experiment
            </h3>

            {/* Content */}
            <div className="space-y-4 text-lg text-stone-300 mb-8" data-id="element-993">
              <p className="text-xl text-stone-100 text-center mb-6" data-id="element-994">
                What would it be worth to you to avoid even ONE major mistake
                with peptides?
              </p>

              <div className="bg-blue-950/30 border-l-4 border-blue-500 p-6 my-6" data-id="element-995">
                <p className="text-xl font-bold text-blue-400 mb-4" data-id="element-996">
                  A bad protocol could cost you:
                </p>
                <div className="space-y-3 text-stone-100" data-id="element-997">
                  <p data-id="element-998">• Months of wasted progress</p>
                  <p data-id="element-999">• Thousands of dollars on bunk products</p>
                  <p data-id="element-1000">• Potential damage to your organs</p>
                  <p data-id="element-1001">
                    • The irreplaceable resource of TIME as your biology
                    continues to decline
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 my-8 rounded-lg" data-id="element-1002">
                <p className="text-xl font-bold text-purple-400 mb-4" data-id="element-1003">
                  PSI PRO pays for itself if it saves you from a single misstep.
                </p>
                <div className="space-y-3 text-stone-100" data-id="element-1004">
                  <p data-id="element-1005">
                    And you're getting 12 months of guidance from the top minds
                    in the field.
                  </p>
                  <p data-id="element-1006">Plus Peptide Mastery 2.0 included free.</p>
                  <p data-id="element-1007">Plus the Precision Cell™ methodology.</p>
                  <p data-id="element-1008">Plus the paradigm shift that changes everything.</p>
                </div>
              </div>

              <div className="text-center mt-8" data-id="element-1009">
                <p className="text-2xl font-bold text-blue-400" data-id="element-1010">Do the math.</p>
              </div>
            </div>

            {/* CTA */}
            <a href="https://go.peptidescienceinstitute.com/pm-pro-annual-fm" className="block w-full px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-center font-bold text-xl rounded-lg transition-all duration-300 shadow-[0_0_40px_rgba(59,130,246,0.5)] hover:shadow-[0_0_60px_rgba(59,130,246,0.7)] hover:scale-105" data-id="element-1011">
              → JOIN NOW BEFORE THE DEADLINE ←
            </a>
          </div>
        </div>

        {/* FOOTER */}
        <div className="relative" data-id="element-1012">
          <div className="text-center space-y-6 py-12 border-t border-purple-500/30" data-id="element-1013">
            <div data-id="element-1014">
              <h4 className="text-3xl font-serif font-bold text-purple-400 mb-2" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 20px rgba(168, 85, 247, 0.4)'
            }} data-id="element-1015">
                The Peptide Science Institute
              </h4>
              <p className="text-lg text-stone-400 italic" data-id="element-1016">
                Rewriting Biology. Transcending Limitations.
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 text-stone-300" data-id="element-1017">
              <Mail className="w-5 h-5 text-purple-400" data-id="element-1018" />
              <p data-id="element-1019">
                Questions? Email us:{' '}
                <a href="mailto:support@support.peptidescienceinstitute.com" className="text-purple-400 hover:text-purple-300 transition-colors underline" data-id="element-1020">
                  support@support.peptidescienceinstitute.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}